import { analytics } from "~/analytics";
import type { Devo } from "~/hooks/usePlayerStore";
import type { Playable } from "~/lib/audioSequence";
import {
  type BookendPlacement,
  type EpisodePlaybackLayout,
  getActiveEpisodePlaybackLayout,
  getBookendAssetIndex,
  getBookendTrackRange,
  getEpisodePlaybackBounds,
} from "~/lib/episode.utils";
import type { PlayerEngineState } from "~/lib/playerEngine";
import { ensureUserData } from "~/stores/reactQuery/useUserQuery";

const IMPRESSION_THRESHOLD_SECONDS = 3;

export type { BookendPlacement } from "~/lib/episode.utils";

type BookendTrackerState = {
  sessionKey: string | null;
  tracked: boolean;
  continuousSeconds: number;
  lastPosition: number | null;
  lastObservedAtMs: number | null;
};

type BookendMeasurementContext = {
  assetSource: string;
  assetTitle: string | null;
  engineState: Pick<PlayerEngineState, "playable" | "playing" | "duration">;
  position: number;
  segmentStart: number;
  segmentEnd: number;
};

type BookendTrackerDeps = {
  getDevo: () => Devo | undefined;
  getEngineState: () => Pick<PlayerEngineState, "playable" | "playing" | "duration">;
  getPosition: () => number;
  isUsingFallbackSequence: () => boolean;
};

function getBookendTrackIndex(
  placement: BookendPlacement,
  assets: NonNullable<Devo["readInAssets"]>,
  layout: EpisodePlaybackLayout,
  playable: Playable,
  position: number,
): number {
  // Composite metadata describes one bookend span, so multiple assets cannot
  // be attributed individually without per-asset boundaries.
  if (layout.mainStartSeconds !== undefined) {
    return assets.length === 1 ? 0 : -1;
  }
  const assetIndex = getBookendAssetIndex(layout, playable, placement, position);
  return assetIndex >= 0 && assetIndex < assets.length ? assetIndex : -1;
}

export class BookendImpressionTracker {
  private trackedSessions = new Set<string>();
  private state: Record<BookendPlacement, BookendTrackerState> = {
    "read-in": {
      sessionKey: null,
      tracked: false,
      continuousSeconds: 0,
      lastPosition: null,
      lastObservedAtMs: null,
    },
    "read-out": {
      sessionKey: null,
      tracked: false,
      continuousSeconds: 0,
      lastPosition: null,
      lastObservedAtMs: null,
    },
  };

  constructor(private deps: BookendTrackerDeps) {}

  resetAll() {
    this.trackedSessions.clear();
    this.reset("read-in");
    this.reset("read-out");
  }

  evaluate(placement: BookendPlacement) {
    const context = this.getMeasurementContext(placement);
    if (!context) {
      this.cancelAccumulation(placement);
      return;
    }

    const { engineState, position, segmentStart, segmentEnd } = context;
    const state = this.state[placement];
    if (!state.sessionKey || state.tracked) {
      return;
    }

    const inBookendSegment = position >= segmentStart && position < segmentEnd;
    if (!inBookendSegment) {
      this.cancelAccumulation(placement);
      return;
    }

    if (!engineState.playing) {
      state.lastPosition = position;
      state.lastObservedAtMs = Date.now();
      return;
    }

    const now = Date.now();
    if (state.lastPosition === null || state.lastObservedAtMs === null) {
      state.lastPosition = position;
      state.lastObservedAtMs = now;
      return;
    }

    const deltaPosition = position - state.lastPosition;
    const deltaSeconds = Math.max(0, (now - state.lastObservedAtMs) / 1000);
    const jumpedAhead = deltaPosition - deltaSeconds > 0.75;

    state.lastPosition = position;
    state.lastObservedAtMs = now;

    if (deltaPosition < 0 || jumpedAhead) {
      state.continuousSeconds = 0;
      return;
    }

    if (deltaPosition === 0) {
      return;
    }

    state.continuousSeconds += deltaPosition;
    if (state.continuousSeconds >= IMPRESSION_THRESHOLD_SECONDS) {
      void this.trackImpression(placement, context);
    }
  }

  private reset(placement: BookendPlacement) {
    this.state[placement] = {
      sessionKey: null,
      tracked: false,
      continuousSeconds: 0,
      lastPosition: null,
      lastObservedAtMs: null,
    };
  }

  private resolveSession(placement: BookendPlacement, nextSessionKey: string | null) {
    const state = this.state[placement];
    if (nextSessionKey === state.sessionKey) {
      return;
    }

    this.state[placement] = {
      sessionKey: nextSessionKey,
      tracked: nextSessionKey !== null && this.trackedSessions.has(nextSessionKey),
      continuousSeconds: 0,
      lastPosition: null,
      lastObservedAtMs: null,
    };
  }

  private cancelAccumulation(placement: BookendPlacement) {
    const state = this.state[placement];
    state.continuousSeconds = 0;
    state.lastPosition = null;
    state.lastObservedAtMs = null;
  }

  private async trackImpression(placement: BookendPlacement, context: BookendMeasurementContext) {
    const devo = this.deps.getDevo();
    const state = this.state[placement];
    const isReadIn = placement === "read-in";
    const event = isReadIn ? "Read-In Impression" : "Read-Out Impression";
    const { assetSource, assetTitle } = context;

    if (!devo || state.tracked || !assetSource) {
      return;
    }

    state.tracked = true;
    if (state.sessionKey) {
      this.trackedSessions.add(state.sessionKey);
    }
    let user: Awaited<ReturnType<typeof ensureUserData>> = null;
    try {
      user = await ensureUserData();
    } catch (_error) {
      // Ignore user lookup failures for impression analytics payload enrichment.
    }
    const planTier = user?.hasDevoPlus ? "Sing+" : "Free";

    analytics.track({
      event,
      properties: {
        placement,
        contentId: devo.id,
        asset_source: assetSource,
        asset_title: assetTitle ?? null,
        series_slug: devo.series?.[0]?.slug?.current ?? null,
        contentTitle: devo.title,
        contentDate: devo.date,
        contentTags: devo.tags,
        plan_tier: planTier,
      },
    });
  }

  private getMeasurementContext(placement: BookendPlacement): BookendMeasurementContext | null {
    const devo = this.deps.getDevo();
    const engineState = this.deps.getEngineState();
    const isReadIn = placement === "read-in";
    if (!devo || !engineState.playable) {
      return null;
    }

    const playable = engineState.playable;
    const position = this.deps.getPosition();
    const layout = getActiveEpisodePlaybackLayout(devo, this.deps.isUsingFallbackSequence());
    const playbackBounds = getEpisodePlaybackBounds({
      progress: position,
      duration: engineState.duration,
      playable: engineState.playable,
      ...layout,
    });
    const legacyReadOutAssets = devo.readOutAssetSource
      ? [{ source: devo.readOutAssetSource, title: devo.readOutAssetTitle ?? null }]
      : [];
    const assets = isReadIn ? (devo.readInAssets ?? []) : (devo.readOutAssets ?? legacyReadOutAssets);
    const trackIndex = getBookendTrackIndex(placement, assets, layout, playable, position);
    const asset = assets[trackIndex];
    if (!asset) {
      return null;
    }
    const assetSource = asset.source;
    const assetTitle = asset.title;
    let segmentStart = isReadIn ? 0 : playbackBounds.mainEndOffset;
    let segmentEnd = isReadIn ? playbackBounds.mainStartOffset : engineState.duration;
    if (layout.mainStartSeconds === undefined) {
      const range = getBookendTrackRange(layout, playable, placement, trackIndex);
      if (!range) return null;
      segmentStart = range.start;
      segmentEnd = range.end;
    }
    const sessionKey = `${placement}:${devo.id}:${trackIndex}:${assetSource}`;
    this.resolveSession(placement, sessionKey);
    if (segmentEnd <= segmentStart) {
      return null;
    }

    return {
      assetSource,
      assetTitle: assetTitle ?? null,
      engineState,
      position,
      segmentStart,
      segmentEnd,
    };
  }
}
